import { useCallback, useMemo, useRef } from 'react';

import { ProducerStatus } from '@/feature/betslip-sbk/types';
import { getEventKeys } from '@/feature/event-details-sbk/hooks/use-event';
import { Supplier } from '@/feature/event-details-sbk/types';
import { getLeagueKeys } from '@/feature/league-hub-sbk/hooks/use-league';
import { useIsSweepsOrPredictionsProduct } from '@/hooks/use-is-sweeps-or-predictions-product';
import { Product } from '@/hooks/use-jurisdiction';
import { useSubscribePublic } from '@/utils/websocket/hooks';
import { ProducerChangeMessage } from '@/utils/websocket/types';
import { useQueryClient } from '@tanstack/react-query';
import { ChannelOptions } from 'ably';

import { getSbkBetSlipStore } from '../use-sbk-betslip-store';

export const useSubscribeProducerStatus = () => {
    const enabled = useIsSweepsOrPredictionsProduct();
    const queryClient = useQueryClient();
    const lastStatuses = useRef<Partial<Record<Supplier, ProducerStatus>>>({});

    const handleProducerChange = useCallback(
        (message: ProducerChangeMessage) => {
            const { supplier, is_up: isUp } = message;
            if (!supplier) {
                return;
            }
            if (isUp && lastStatuses.current[supplier] === 'DOWN') {
                queryClient.invalidateQueries({ queryKey: getEventKeys().all });
                queryClient.invalidateQueries({ queryKey: getLeagueKeys().all });
            }
            const status: ProducerStatus = isUp ? 'UP' : 'DOWN';
            lastStatuses.current[supplier] = status;
            // Both product stores get the update; the producer being down is not product-specific
            ([Product.Sweeps, Product.Prediction] as const).forEach(product => {
                getSbkBetSlipStore(product).getState().actions.updateProducerStatus(supplier, status);
            });
        },
        [queryClient]
    );

    const values = useMemo<{
        params: {};
        eventName: ['producer_up', 'producer_down'];
        options: ChannelOptions;
    }>(() => {
        return {
            params: {},
            options: { params: { rewind: '1' } },
            eventName: ['producer_up', 'producer_down'],
        };
    }, []);

    useSubscribePublic<'systemProducerChange'>(
        'system:producer',
        values.params,
        values.eventName,
        handleProducerChange,
        enabled,
        values.options
    );
};
